import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { BrowserRouter as Router, Navigate, Route, Routes, useParams } from 'react-router-dom'
import { AppShell } from '@/components/AppShell'
import { RouteAwareErrorBoundary } from '@/components/ErrorBoundary'
import { RequireAuth } from '@/components/RequireAuth'
import { RequireModule } from '@/components/RequireModule'
import { RequireUnit } from '@/components/RequireUnit'
import { RequireRoles } from '@/components/RequireRoles'
import { canUseAllUnitsMode, getAccessFlags } from '@/lib/access'
import { useAuthStore } from '@/stores/authStore'
import { useUnitStore } from '@/stores/unitStore'

const Home = lazy(() => import('@/pages/Home'))
const Login = lazy(() => import('@/pages/Login'))
const ChangePassword = lazy(() => import('@/pages/ChangePassword'))
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'))
const ResetPassword = lazy(() => import('@/pages/ResetPassword'))
const Manual = lazy(() => import('@/pages/Manual'))
const ManagerGuide = lazy(() => import('@/pages/ManagerGuide'))
const TeamAgenda = lazy(() => import('@/pages/TeamAgenda'))
// AbsenceCalendar foi unificado em TeamAgenda (calendário único da equipe).
// A rota /app/absences/calendar continua existindo, mas agora redireciona pra /app/agenda.
const BillsPage = lazy(() => import('@/pages/Bills'))
const Calendar = lazy(() => import('@/pages/Calendar'))
const CardOperators = lazy(() => import('@/pages/CardOperators'))
const LegalEntities = lazy(() => import('@/pages/LegalEntities'))
const PaymentTerminals = lazy(() => import('@/pages/PaymentTerminals'))
const CaixaBalcao = lazy(() => import('@/pages/caixa/CaixaBalcao'))
const GerenteContagem = lazy(() => import('@/pages/caixa/GerenteContagem'))
const BankAccounts = lazy(() => import('@/pages/BankAccounts'))
const CollaboratorAnalytics = lazy(() => import('@/pages/CollaboratorAnalytics'))
const CollaboratorCosts = lazy(() => import('@/pages/CollaboratorCosts'))
const Payroll = lazy(() => import('@/pages/Payroll'))
const PayrollDetail = lazy(() => import('@/pages/PayrollDetail'))
const CollaboratorPromote = lazy(() => import('@/pages/CollaboratorPromote'))
const MonthlyClosing = lazy(() => import('@/pages/MonthlyClosing'))
const BankReconciliation = lazy(() => import('@/pages/BankReconciliation'))
const TimeRecords = lazy(() => import('@/pages/TimeRecords'))
const AtsKanban = lazy(() => import('@/pages/AtsKanban'))
const AtsBlacklist = lazy(() => import('@/pages/AtsBlacklist'))
const AtsDashboard = lazy(() => import('@/pages/AtsDashboard'))
const AtsRepechage = lazy(() => import('@/pages/AtsRepechage'))
const AtsQuestions = lazy(() => import('@/pages/AtsQuestions'))
const AtsSlots = lazy(() => import('@/pages/AtsSlots'))
const AtsAdmission = lazy(() => import('@/pages/AtsAdmission'))
const CareersLayout = lazy(() => import('@/modules/careers/CareersLayout'))
const CareersList = lazy(() => import('@/modules/careers/pages/CareersList'))
const CareersApply = lazy(() => import('@/modules/careers/pages/CareersApply'))
const CareersTracking = lazy(() => import('@/modules/careers/pages/CareersTracking'))
const CareersVideo = lazy(() => import('@/modules/careers/pages/CareersVideo'))
const Provisions = lazy(() => import('@/pages/Provisions'))
const Performance = lazy(() => import('@/pages/Performance'))
const CollaboratorProfile = lazy(() => import('@/pages/CollaboratorProfile'))
const Collaborators = lazy(() => import('@/pages/Collaborators'))
const CollaboratorsBatch = lazy(() => import('@/pages/CollaboratorsBatch'))
const ExtraWorkersPage = lazy(() => import('@/pages/ExtraWorkers'))
const Consumptions = lazy(() => import('@/pages/Consumptions'))
const AccessModules = lazy(() => import('@/pages/AccessModules'))
const Cooperatives = lazy(() => import('@/pages/Cooperatives'))
const ClosingRedirect = lazy(() => import('@/pages/ClosingRedirect'))
const DailyPanel = lazy(() => import('@/pages/DailyPanel'))
const DailyConsolidated = lazy(() => import('@/pages/DailyConsolidated'))
const DailyClosingGrid = lazy(() => import('@/pages/DailyClosingGrid'))
const DailyClosingWizard = lazy(() => import('@/pages/DailyClosingWizard'))
const MotoboysPanel = lazy(() => import('@/pages/MotoboysPanel'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const WorkCalendar = lazy(() => import('@/pages/WorkCalendar'))
const MySchedule = lazy(() => import('@/pages/MySchedule'))
const AdminHome = lazy(() => import('@/pages/AdminHome'))
const ImportPage = lazy(() => import('@/pages/Import'))
const Menu = lazy(() => import('@/pages/Menu'))
const CardapioImport = lazy(() => import('@/pages/CardapioImport'))
const MobileHub = lazy(() => import('@/pages/MobileHub'))
const MobileApp = lazy(() => import('@/pages/MobileApp'))
const PinSetup = lazy(() => import('@/pages/PinSetup'))
const MobileMotoboy = lazy(() => import('@/pages/MobileMotoboy'))
const MotoboyClosings = lazy(() => import('@/pages/MotoboyClosings'))
const MotoboyExpedicao = lazy(() => import('@/pages/MotoboyExpedicao'))
const MyPanel = lazy(() => import('@/pages/MyPanel'))
const NewAbsence = lazy(() => import('@/pages/NewAbsence'))
const ParametersPage = lazy(() => import('@/pages/Parameters'))
const Purchases = lazy(() => import('@/pages/Purchases'))
const Reports = lazy(() => import('@/pages/Reports'))
const ResourcesPage = lazy(() => import('@/pages/Resources'))
const VouchersPage = lazy(() => import('@/modules/vouchers/pages/VouchersPage'))
const CompanyRolesAdmin = lazy(() => import('@/pages/CompanyRolesAdmin'))
const WifiVisitorsPage = lazy(() => import('@/modules/wifi/pages/WifiVisitorsPage'))
const WifiVisitorDetailPage = lazy(() => import('@/modules/wifi/pages/WifiVisitorDetailPage'))
const WifiCashierPage = lazy(() => import('@/modules/wifi/pages/WifiCashierPage'))
const WifiDenylistPage = lazy(() => import('@/modules/wifi/pages/WifiDenylistPage'))
const WifiAttendantRankingPage = lazy(() => import('@/modules/wifi/pages/WifiAttendantRankingPage'))
const WifiFeedbackInboxPage = lazy(() => import('@/modules/wifi/pages/WifiFeedbackInboxPage'))
const WifiSettingsPage = lazy(() => import('@/modules/wifi/pages/WifiSettingsPage'))
const VoucherDesignerPage = lazy(() => import('@/modules/vouchers/pages/VoucherDesignerPage'))
const VoucherPartnerPortal = lazy(() => import('@/modules/vouchers/pages/VoucherPartnerPortalPage'))
const VoucherPublicLookupPage = lazy(() => import('@/modules/vouchers/pages/VoucherPublicLookupPage'))
// SectorClosing aposentado em favor do ManagerClosing (/closing-pro).
// Mantemos um redirect (SectorRedirect abaixo) p/ preservar bookmarks.
// const SectorClosing = lazy(() => import('@/pages/SectorClosing'))

function SectorRedirect() {
  const { date } = useParams<{ date?: string }>()
  return <Navigate to={`/app/closing-pro${date ? `/${date}` : ''}`} replace />
}
const ManagerClosing = lazy(() => import('@/pages/ManagerClosing'))
// Dia do Colaborador (F1 — docs/_archive/PLANO_DIA_DO_COLABORADOR.md)
const DiaHome = lazy(() => import('@/pages/dia/DiaHome'))
const AbrirDia = lazy(() => import('@/pages/dia/AbrirDia'))
const FecharDia = lazy(() => import('@/pages/dia/FecharDia'))
const DiaHistorico = lazy(() => import('@/pages/dia/DiaHistorico'))
const RondaDia = lazy(() => import('@/pages/dia/RondaDia'))
const RotinaPeriodica = lazy(() => import('@/pages/dia/RotinaPeriodica'))
const ContagemEstoque = lazy(() => import('@/pages/dia/ContagemEstoque'))
const DiaRotinasConfig = lazy(() => import('@/pages/equipe/DiaRotinasConfig'))
const RankingLojas = lazy(() => import('@/pages/dia/RankingLojas'))
const MetasEquipe = lazy(() => import('@/pages/equipe/MetasEquipe'))
const DiaScreen = lazy(() => import('@/components/dia/DiaScreen'))
const DiaEquipe = lazy(() => import('@/pages/equipe/DiaEquipe'))
const ChecklistTemplates = lazy(() => import('@/pages/equipe/ChecklistTemplates'))
const ChecklistTemplateEditor = lazy(() => import('@/pages/equipe/ChecklistTemplateEditor'))
const ExtrasCentral = lazy(() => import('@/pages/equipe/ExtrasCentral'))
const ExtraDetalhe = lazy(() => import('@/pages/equipe/ExtraDetalhe'))
const PersonProfile = lazy(() => import('@/pages/PersonProfile'))
const PersonSearch = lazy(() => import('@/pages/PersonProfile').then((m) => ({ default: m.PersonSearch })))
// F3.6 — home mobile do gerente (rota OPT-IN, não substitui a desktop /closing-pro)
const ManagerClosingMobile = lazy(() => import('@/pages/mobile/ManagerClosingMobile'))
const Suppliers = lazy(() => import('@/pages/Suppliers'))
const SchedulePage = lazy(() => import('@/pages/SchedulePage'))
const Swaps = lazy(() => import('@/pages/Swaps'))
const UnitsPage = lazy(() => import('@/pages/Units'))
const UsersPage = lazy(() => import('@/pages/Users'))
const UsersAdminPage = lazy(() => import('@/pages/UsersAdmin'))
const UserPermissionsDebugPage = lazy(() => import('@/pages/UserPermissionsDebug'))
const UserAuditTimelinePage = lazy(() => import('@/pages/UserAuditTimeline'))
const RolePermissionsPage = lazy(() => import('@/pages/RolePermissions'))
const CrmInbox = lazy(() => import('@/pages/crm/Inbox'))
const PermissionGroupsPage = lazy(() => import('@/pages/PermissionGroups'))
const WeeklyClosing = lazy(() => import('@/pages/WeeklyClosing'))
const MotoboyPayables = lazy(() => import('@/pages/MotoboyPayables'))
const AuditLogPage = lazy(() => import('@/pages/AuditLog'))
const CredentialVaultPage = lazy(() => import('@/pages/CredentialVault'))
const OnboardingChecklistPage = lazy(() => import('@/pages/OnboardingChecklist'))
const Expenses = lazy(() => import('@/pages/Expenses'))
const ExpenseCategoriesPage = lazy(() => import('@/pages/ExpenseCategoriesPage'))
const PaymentMethodsPage = lazy(() => import('@/pages/PaymentMethodsPage'))
const FiadosPendentes = lazy(() => import('@/pages/FiadosPendentes'))
const JobTitlesPage = lazy(() => import('@/pages/JobTitlesPage'))
const ProductCatalogPage = lazy(() => import('@/pages/ProductCatalogPage'))
const VoucherTerminalPage = lazy(() => import('@/pages/VoucherTerminalPage'))
const VouchersGerentePage = lazy(() => import('@/pages/VouchersGerente'))
const VoucherCounterSalePage = lazy(() => import('@/pages/VoucherCounterSale'))
const VoucherSalesReportPage = lazy(() => import('@/pages/VoucherSalesReport'))
const VoucherUnusedPage = lazy(() => import('@/pages/VoucherUnused'))
const SettingsRegistrationsPage = lazy(() => import('@/pages/SettingsRegistrations'))
const PostsaleCancellationsPage = lazy(() => import('@/pages/PostsaleCancellations'))
const CortesiasSequenciaPage = lazy(() => import('@/pages/CortesiasSequencia'))
const AniversariantesPage = lazy(() => import('@/pages/Aniversariantes'))
const CortesiasGiftCardsAdmin = lazy(() => import('@/pages/CortesiasGiftCardsAdmin'))
const CooperativePortalLayout = lazy(() => import('@/modules/cooperative/CooperativePortalLayout'))
const CooperativeHome = lazy(() => import('@/modules/cooperative/pages/CooperativeHome'))
const CooperativeClosings = lazy(() => import('@/modules/cooperative/pages/CooperativeClosings'))
const CooperativeSummary = lazy(() => import('@/modules/cooperative/pages/CooperativeSummary'))
const PartnerLogin = lazy(() => import('@/modules/partner/pages/PartnerLogin'))
const PartnerPortalWrapper = lazy(() => import('@/modules/partner/PartnerPortalWrapper'))
const PartnerHome = lazy(() => import('@/modules/partner/pages/PartnerHome'))
const PartnerVouchers = lazy(() => import('@/modules/partner/pages/PartnerVouchers'))
const PartnerSummary = lazy(() => import('@/modules/partner/pages/PartnerSummary'))
const MotoboyPanelLayout = lazy(() => import('@/modules/motoboy/MotoboyPanelLayout'))
const MotoboyHome = lazy(() => import('@/modules/motoboy/pages/MotoboyHome'))
const MotoboyClosingsList = lazy(() => import('@/modules/motoboy/pages/MotoboyClosingsList'))
const MotoboyConfirmation = lazy(() => import('@/modules/motoboy/pages/MotoboyConfirmation'))

function Wrap({ children }: { children: ReactNode }) {
  return <RouteAwareErrorBoundary>{children}</RouteAwareErrorBoundary>
}

function PageFallback() {
  return <div className="grid min-h-[30vh] place-items-center text-sm text-zinc-500">Carregando...</div>
}

function withSuspense(children: ReactNode) {
  return (
    <Wrap>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </Wrap>
  )
}

function AppHomeRedirect() {
  const selectedUnitId = useUnitStore((s) => s.selectedUnitId)
  const rolesByUnit = useUnitStore((s) => s.rolesByUnit)
  const loadRoles = useUnitStore((s) => s.loadRoles)
  const isMotoboy = useAuthStore((s) => s.user?.isMotoboy ?? false)
  // Colaborador comum com o módulo "Dia do colaborador" ligado cai direto no
  // seu dia (grupo focal, melhoria 1) — o hub fica a 1 toque no rodapé.
  const goesToDia = useAuthStore((s) => Boolean(s.user?.modules?.includes('dia_colaborador') && s.user?.collaboratorId && !s.user?.isMotoboy))

  useEffect(() => {
    if (!selectedUnitId) return
    void loadRoles(selectedUnitId)
  }, [selectedUnitId, loadRoles])

  if (!selectedUnitId) {
    // As permissões de cada unidade chegam depois (AppShell carrega no modo
    // "Todas as unidades"); decidir antes mandava o dono para /consumptions,
    // que exige unidade — tela em branco.
    const units = useUnitStore.getState().units
    if (!useUnitStore.getState().unitsLoaded || units.some((u) => !rolesByUnit[u.id])) {
      return (
        <div className="grid min-h-[30vh] place-items-center text-sm text-zinc-500">
          Carregando permissões...
        </div>
      )
    }
    // Owner in "All units" mode → land on Dashboard (which renders all-units view)
    // Home do admin com KPIs (pedido do David, 01/11/2026).
    if (canUseAllUnitsMode(rolesByUnit)) {
      return <Navigate to="/app/inicio" replace />
    }
    // Quem não pode usar "Todas as unidades" sem unidade selecionada (ex.:
    // colaborador recém-criado) ganha a primeira unidade em que tem papel —
    // senão caía em /app/consumptions, que o RequireRoles devolvia para cá
    // em loop (?next=?next=...).
    const firstWithRole = units.find((u) => (rolesByUnit[u.id] ?? []).length > 0)
    if (firstWithRole) {
      useUnitStore.getState().selectUnit(firstWithRole.id)
      return (
        <div className="grid min-h-[30vh] place-items-center text-sm text-zinc-500">
          Carregando permissões...
        </div>
      )
    }
    return <Navigate to="/app/consumptions" replace />
  }

  const roles = rolesByUnit[selectedUnitId]
  if (!roles) {
    return (
      <div className="grid min-h-[30vh] place-items-center text-sm text-zinc-500">
        Carregando permissões...
      </div>
    )
  }

  const access = getAccessFlags(roles)
  if (access.isAdmin) return <Navigate to="/app/inicio" replace />
  if (access.canViewDashboard) return <Navigate to="/app/dashboard" replace />
  // Atendente de expedição: home é a tela da expedição (caixa dos motoboys)
  if (access.isExpedicaoOnly) return <Navigate to="/app/expedicao" replace />
  if (access.isEmployeeOnly) {
    if (isMotoboy) return <Navigate to="/meu-painel" replace />
    if (goesToDia) return <Navigate to="/app/dia" replace />
    return <Navigate to="/app/mobile" replace />
  }
  return <Navigate to="/app/consumptions" replace />
}

/**
 * Subdomínio rh.4queijos.com serve APENAS o portal externo de candidatura.
 * Qualquer rota não-/carreiras nesse host é redirecionada para /carreiras.
 */
function isCareersHost(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.hostname.startsWith('rh.')
}

export default function App() {
  // Hard redirect antes do Router montar: hosts "rh.*" só veem /carreiras
  if (isCareersHost() && !window.location.pathname.startsWith('/carreiras')) {
    window.location.replace('/carreiras' + window.location.search)
    return null
  }

  return (
    <Router>
      <Routes>
        <Route path="/carreiras" element={withSuspense(<CareersLayout />)}>
          <Route index element={withSuspense(<CareersList />)} />
          <Route path="vaga/:id" element={withSuspense(<CareersApply />)} />
          <Route path="banco-talentos" element={withSuspense(<CareersApply />)} />
          <Route path="aplicacao/:token" element={withSuspense(<CareersTracking />)} />
          <Route path="video/:groupToken" element={withSuspense(<CareersVideo />)} />
        </Route>
        <Route path="/" element={withSuspense(<Home />)} />
        <Route path="/login" element={withSuspense(<Login />)} />
        <Route path="/forgot-password" element={withSuspense(<ForgotPassword />)} />
        <Route path="/reset-password" element={withSuspense(<ResetPassword />)} />
        <Route path="/voucher" element={withSuspense(<VoucherPublicLookupPage />)} />
        <Route path="/portal/vouchers" element={withSuspense(<VoucherPartnerPortal />)} />
        {/* PWA: troca obrigatória do PIN inicial — fora do AppShell, porque com
            a troca pendente o servidor barra tudo que o shell carrega (403). */}
        <Route
          path="/pin"
          element={
            <RequireAuth>
              <div className="min-h-dvh bg-background px-4 py-8 text-foreground pt-safe pb-safe">
                {withSuspense(<PinSetup />)}
              </div>
            </RequireAuth>
          }
        />
        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
          >
            <Route
            path="consumptions"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {/* lazy() precisa de Suspense: sem ele, abrir /app/consumptions
                    direto quebrava com "A component suspended while responding
                    to synchronous input" (cartilha, item 6). */}
                {withSuspense(<Consumptions />)}
              </RequireRoles>
            }
          />
          <Route index element={<AppHomeRedirect />} />
          <Route path="change-password" element={withSuspense(<ChangePassword />)} />
          {/* PWA: definir/trocar PIN de 4 dígitos e celulares lembrados (migration 180). */}
          <Route path="pin" element={withSuspense(<PinSetup />)} />
            <Route
              path="mobile"
              element={withSuspense(<MobileHub />)}
            />
            {/* APK Flutter congelado (29/10/2026): tela só com o módulo apk_android ligado. */}
            <Route
              path="aplicativo"
              element={
                <RequireModule moduleKey="apk_android" fallbackTo="/app">
                  {withSuspense(<MobileApp />)}
                </RequireModule>
              }
            />
            <Route
              path="manual"
              element={withSuspense(<Manual />)}
            />
            <Route
              path="guia-gerente"
              element={
                <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                  {withSuspense(<ManagerGuide />)}
                </RequireRoles>
              }
            />
          <Route
            path="inicio"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<AdminHome />)}
              </RequireRoles>
            }
          />
          <Route
            path="dashboard"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Dashboard />)}
              </RequireRoles>
            }
          />
          <Route
            path="menu"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Menu />)}
              </RequireRoles>
            }
          />
          <Route
            path="cardapio/import"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<CardapioImport />)}
              </RequireRoles>
            }
          />
          <Route
            path="import"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<ImportPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="calendar"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Calendar />)}
              </RequireRoles>
            }
          />
          <Route
            path="daily-panel"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<DailyPanel />)}
              </RequireRoles>
            }
          />
          <Route
            path="closing/:date"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<ClosingRedirect />)}
              </RequireRoles>
            }
          />
          <Route
            path="closing/view/:closingId"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<DailyConsolidated />)}
              </RequireRoles>
            }
          />
          <Route
            path="closing/:date/grid"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<DailyClosingGrid />)}
              </RequireRoles>
            }
          />
          <Route
            path="closing/:date/wizard"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<DailyClosingWizard />)}
              </RequireRoles>
            }
          />
          <Route
            path="motoboys"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<MotoboysPanel />)}
              </RequireRoles>
            }
          />
          {/* DEPRECATED: /sector/:date redireciona pro Manager (/closing-pro).
              Mantemos a rota pra nao quebrar links externos/bookmarks; o
              ManagerClosing eh a tela canonica de fechamento do gerente.
              Quando ninguem mais usar bookmarks de /sector, podemos remover. */}
          <Route
            path="sector/:date?"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                <SectorRedirect />
              </RequireRoles>
            }
          />
          <Route
            path="closing-pro/:date?"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<ManagerClosing />)}
              </RequireRoles>
            }
          />
          {/* F3.6 — home mobile do gerente (OPT-IN, acessível por link/QR).
              Mesma RBAC da /closing-pro; só vira default após validação em
              celular real com a gerente (condição do protótipo aprovado). */}
          <Route
            path="m/closing/:date?"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<ManagerClosingMobile />)}
              </RequireRoles>
            }
          />
          {/* Telas simples do fechamento: caixa do balcão (valor por maquininha)
              e contagem do gerente. Gravam por PATCH só a parte delas. */}
          <Route
            path="caixa/:date?"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<CaixaBalcao />)}
              </RequireRoles>
            }
          />
          <Route
            path="contagem/:date?"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<GerenteContagem />)}
              </RequireRoles>
            }
          />
          <Route
            path="weekly/:weekStart?"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                <RequireUnit label="a visão da semana">{withSuspense(<WeeklyClosing />)}</RequireUnit>
              </RequireRoles>
            }
          />
          <Route
            path="motoboy"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor', 'atendente_expedicao']}>
                {withSuspense(<MotoboyClosings />)}
              </RequireRoles>
            }
          />
          <Route
            path="expedicao"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor', 'atendente_expedicao']}>
                {withSuspense(<MotoboyExpedicao />)}
              </RequireRoles>
            }
          />
          <Route
            path="contas"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<MotoboyPayables />)}
              </RequireRoles>
            }
          />
          <Route
            path="expenses"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Expenses />)}
              </RequireRoles>
            }
          />
          <Route
            path="expense-categories"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<ExpenseCategoriesPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="payment-methods"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<PaymentMethodsPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="fiados"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<FiadosPendentes />)}
              </RequireRoles>
            }
          />
          <Route
            path="job-titles"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<JobTitlesPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="product-catalog"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<ProductCatalogPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="pos-venda/cancelamentos"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<PostsaleCancellationsPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="voucher-terminal"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor', 'employee']}>
                {withSuspense(<VoucherTerminalPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="cortesias-giftcards"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<CortesiasGiftCardsAdmin />)}
              </RequireRoles>
            }
          />
          <Route
            path="collaborators"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Collaborators />)}
              </RequireRoles>
            }
          />
          <Route
            path="collaborators/batch"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<CollaboratorsBatch />)}
              </RequireRoles>
            }
          />
          <Route
            path="collaborators/:id/costs"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<CollaboratorCosts />)}
              </RequireRoles>
            }
          />
          <Route
            path="payroll"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<Payroll />)}
              </RequireRoles>
            }
          />
          <Route
            path="payroll/:periodId"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<PayrollDetail />)}
              </RequireRoles>
            }
          />
          <Route
            path="collaborators/:id/promote"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<CollaboratorPromote />)}
              </RequireRoles>
            }
          />
          <Route
            path="closing/monthly"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                <RequireUnit label="o fechamento mensal">{withSuspense(<MonthlyClosing />)}</RequireUnit>
              </RequireRoles>
            }
          />
          <Route
            path="bank-reconciliation"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<BankReconciliation />)}
              </RequireRoles>
            }
          />
          <Route
            path="time-records"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<TimeRecords />)}
              </RequireRoles>
            }
          />
          <Route
            path="ats"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<AtsKanban />)}
              </RequireRoles>
            }
          />
          <Route
            path="ats/blacklist"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<AtsBlacklist />)}
              </RequireRoles>
            }
          />
          <Route
            path="ats/dashboard"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<AtsDashboard />)}
              </RequireRoles>
            }
          />
          <Route
            path="ats/repescagem"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<AtsRepechage />)}
              </RequireRoles>
            }
          />
          <Route
            path="ats/perguntas"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<AtsQuestions />)}
              </RequireRoles>
            }
          />
          <Route
            path="ats/slots"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<AtsSlots />)}
              </RequireRoles>
            }
          />
          <Route
            path="ats/admissao"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<AtsAdmission />)}
              </RequireRoles>
            }
          />
          <Route
            path="provisions"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Provisions />)}
              </RequireRoles>
            }
          />
          <Route
            path="performance"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Performance />)}
              </RequireRoles>
            }
          />
          <Route
            path="collaborators/analytics"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<CollaboratorAnalytics />)}
              </RequireRoles>
            }
          />
          <Route
            path="collaborators/new-absence"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<NewAbsence />)}
              </RequireRoles>
            }
          />
          <Route
            path="collaborators/:id/profile"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<CollaboratorProfile />)}
              </RequireRoles>
            }
          />
          <Route
            path="extras"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<ExtraWorkersPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="agenda"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<TeamAgenda />)}
              </RequireRoles>
            }
          />
          {/* DEPRECATED: /absences/calendar → redireciona pra /agenda (calendário único da equipe).
              Mantemos pra não quebrar bookmarks/links externos. */}
          <Route
            path="absences/calendar"
            element={<Navigate to="/app/agenda" replace />}
          />
          <Route
            path="absences/swaps"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Swaps />)}
              </RequireRoles>
            }
          />
          <Route
            path="escala"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<SchedulePage />)}
              </RequireRoles>
            }
          />
          <Route
            path="calendario-trabalho"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<WorkCalendar />)}
              </RequireRoles>
            }
          />
          <Route path="my-schedule" element={withSuspense(<MySchedule />)} />
          <Route
            path="escala/historico"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Swaps />)}
              </RequireRoles>
            }
          />
          <Route
            path="cooperatives"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Cooperatives />)}
              </RequireRoles>
            }
          />
          <Route
            path="suppliers"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Suppliers />)}
              </RequireRoles>
            }
          />
          <Route
            path="purchases"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Purchases />)}
              </RequireRoles>
            }
          />
          {/* Dia do Colaborador — colaborador (PWA) */}
          {['dia', 'dia/abrir', 'dia/fechar', 'dia/historico', 'dia/ronda', 'dia/estoque', 'dia/rotina/:id', 'dia/ranking'].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireRoles allow={['employee', 'owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                  <RequireModule moduleKey="dia_colaborador" fallbackTo="/app/mobile">
                    {withSuspense(
                      <DiaScreen>
                        {path === 'dia' ? <DiaHome />
                          : path === 'dia/abrir' ? <AbrirDia />
                          : path === 'dia/fechar' ? <FecharDia />
                          : path === 'dia/ronda' ? <RondaDia />
                          : path === 'dia/estoque' ? <ContagemEstoque />
                          : path === 'dia/rotina/:id' ? <RotinaPeriodica />
                          : path === 'dia/ranking' ? <RankingLojas />
                          : <DiaHistorico />}
                      </DiaScreen>,
                    )}
                  </RequireModule>
                </RequireRoles>
              }
            />
          ))}
          {/* Dia do Colaborador — painel do gerente */}
          <Route
            path="equipe/dia"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<DiaEquipe />)}
              </RequireRoles>
            }
          />
          <Route
            path="equipe/dia/rotinas"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<DiaRotinasConfig />)}
              </RequireRoles>
            }
          />
          <Route
            path="equipe/metas"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<MetasEquipe />)}
              </RequireRoles>
            }
          />
          <Route
            path="equipe/checklists"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<ChecklistTemplates />)}
              </RequireRoles>
            }
          />
          <Route
            path="equipe/checklists/:id"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<ChecklistTemplateEditor />)}
              </RequireRoles>
            }
          />
          {/* Central de Extras — lista da rede + relatório de acompanhamento por pessoa */}
          <Route
            path="equipe/extras"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<ExtrasCentral />)}
              </RequireRoles>
            }
          />
          <Route
            path="pessoas"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<PersonSearch />)}
              </RequireRoles>
            }
          />
          <Route
            path="pessoas/:cpf"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<PersonProfile />)}
              </RequireRoles>
            }
          />
          <Route
            path="equipe/extras/:id"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<ExtraDetalhe />)}
              </RequireRoles>
            }
          />
          <Route
            path="my-panel"
            element={
              <RequireRoles allow={['employee']}>
                {withSuspense(<MyPanel />)}
              </RequireRoles>
            }
          />
          <Route
            path="reports"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<Reports />)}
              </RequireRoles>
            }
          />
          <Route
            path="recursos"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<ResourcesPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="empresas"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<CompanyRolesAdmin />)}
              </RequireRoles>
            }
          />
          <Route
            path="vouchers"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                <RequireModule moduleKey="vouchers">
                  {withSuspense(<VouchersPage />)}
                </RequireModule>
              </RequireRoles>
            }
          />
          <Route
            path="vouchers/novo"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<VouchersGerentePage />)}
              </RequireRoles>
            }
          />
          {/* Venda no balcão: o backend decide (papel na unidade ou permissão vouchers.sell — ex.: caixa). */}
          <Route path="vouchers/vender" element={withSuspense(<VoucherCounterSalePage />)} />
          <Route
            path="vouchers/nao-usados"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<VoucherUnusedPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="vouchers/relatorio-vendas"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<VoucherSalesReportPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/sequencia-cortesia"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<CortesiasSequenciaPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="aniversariantes"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<AniversariantesPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/cadastros"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<SettingsRegistrationsPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="vouchers/designer"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                <RequireModule moduleKey="vouchers">
                  {withSuspense(<VoucherDesignerPage />)}
                </RequireModule>
              </RequireRoles>
            }
          />
          <Route
            path="wifi/visitors"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                <RequireModule moduleKey="wifi">
                  {withSuspense(<WifiVisitorsPage />)}
                </RequireModule>
              </RequireRoles>
            }
          />
          <Route
            path="wifi/visitors/:id"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                <RequireModule moduleKey="wifi">
                  {withSuspense(<WifiVisitorDetailPage />)}
                </RequireModule>
              </RequireRoles>
            }
          />
          <Route
            path="wifi/cashier"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                <RequireModule moduleKey="wifi">
                  {withSuspense(<WifiCashierPage />)}
                </RequireModule>
              </RequireRoles>
            }
          />
          <Route
            path="wifi/denylist"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                <RequireModule moduleKey="wifi">
                  {withSuspense(<WifiDenylistPage />)}
                </RequireModule>
              </RequireRoles>
            }
          />
          <Route
            path="wifi/ranking"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                <RequireModule moduleKey="wifi">
                  {withSuspense(<WifiAttendantRankingPage />)}
                </RequireModule>
              </RequireRoles>
            }
          />
          <Route
            path="wifi/feedback"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                <RequireModule moduleKey="wifi">
                  {withSuspense(<WifiFeedbackInboxPage />)}
                </RequireModule>
              </RequireRoles>
            }
          />
          <Route
            path="wifi/settings"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                <RequireModule moduleKey="wifi">
                  {withSuspense(<WifiSettingsPage />)}
                </RequireModule>
              </RequireRoles>
            }
          />
          <Route
            path="settings/units"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<UnitsPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/card-operators"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<CardOperators />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/maquininhas"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<PaymentTerminals />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/legal-entities"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<LegalEntities />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/bank-accounts"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<BankAccounts />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/parameters"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<ParametersPage />)}
              </RequireRoles>
            }
          />
          {/* Acesso → Módulos: liga/desliga app_modules da rede (cartilha, item 7). */}
          <Route
            path="acesso/modulos"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<AccessModules />)}
              </RequireRoles>
            }
          />
          <Route
            path="users"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<UsersPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/role-permissions"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<RolePermissionsPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="crm/inbox"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade', 'supervisor']}>
                {withSuspense(<CrmInbox />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/groups"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<PermissionGroupsPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/users"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<UsersAdminPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/users/:userId/permissions"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<UserPermissionsDebugPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="settings/users/:userId/audit-timeline"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<UserAuditTimelinePage />)}
              </RequireRoles>
            }
          />
          <Route
            path="bills"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<BillsPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="onboarding"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<OnboardingChecklistPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="auditoria"
            element={
              <RequireRoles allow={['owner', 'admin_rede']}>
                {withSuspense(<AuditLogPage />)}
              </RequireRoles>
            }
          />
          <Route
            path="cofre"
            element={
              <RequireRoles allow={['owner', 'admin_rede', 'gerente_unidade']}>
                {withSuspense(<CredentialVaultPage />)}
              </RequireRoles>
            }
          />
        </Route>
        <Route path="/portal-cooperativa" element={withSuspense(<CooperativePortalLayout />)}>
          <Route index element={withSuspense(<CooperativeHome />)} />
          <Route path="fechamentos" element={withSuspense(<CooperativeClosings />)} />
          <Route path="resumo" element={withSuspense(<CooperativeSummary />)} />
        </Route>
        <Route path="/portal-parceiro/login" element={withSuspense(<PartnerLogin />)} />
        <Route path="/portal-parceiro/*" element={withSuspense(<PartnerPortalWrapper />)}>
          <Route index element={withSuspense(<PartnerHome />)} />
          <Route path="vouchers" element={withSuspense(<PartnerVouchers />)} />
          <Route path="resumo" element={withSuspense(<PartnerSummary />)} />
        </Route>
        <Route path="/meu-painel" element={withSuspense(<MotoboyPanelLayout />)}>
          <Route index element={withSuspense(<MotoboyHome />)} />
          <Route path="fechamentos" element={withSuspense(<MotoboyClosingsList />)} />
          <Route path="fechamentos/:id/confirmar" element={withSuspense(<MotoboyConfirmation />)} />
        </Route>
        <Route
          path="/m/motoboy"
          element={
            <RequireAuth>
              <RequireRoles allow={['employee']}>
                {withSuspense(<MobileMotoboy />)}
              </RequireRoles>
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  )
}
